import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Checkbox,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
} from '@mui/material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import debounce from 'lodash.debounce';
import { fieldConfigApi } from '@/services/api';
import { MasterConfig, MasterField, FieldOption } from './types';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { Surface, Button, Icon, Pill, Note, confirmColors } from '@/components/ui';

interface FieldConfigMasterProps {
    config: MasterConfig;
}

const FieldConfigMaster: React.FC<FieldConfigMasterProps> = ({ config }) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditItem] = useState<any | null>(null);
    const [formData, setFormData] = useState<any>({});
    const [availableFields, setAvailableFields] = useState<FieldOption[]>([]);
    const fieldsLoadedRef = useRef(false);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    const updateSearchQuery = React.useMemo(
        () => debounce((value: string) => {
            setDebouncedSearchTerm(value);
            setPage(0);
        }, 500),
        []
    );

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        updateSearchQuery(value);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Available fields are static — load them once, alongside the list
            // rather than after it (and without re-creating fetchData, which used
            // to trigger a second list fetch on mount).
            const needFields = !fieldsLoadedRef.current;
            const [response, fields] = await Promise.all([
                config.api.list(),
                needFields ? fieldConfigApi.getAvailableFields() : Promise.resolve(null),
            ]);
            setData(Array.isArray(response) ? response : (response.results || []));

            if (fields) {
                fieldsLoadedRef.current = true;
                setAvailableFields(fields.map(f => ({ id: f.value, name: f.label })));
            }
        } catch (err) {
            console.error(err);
            toast.error(`Failed to fetch ${config.label}`);
        } finally {
            setLoading(false);
        }
    }, [config]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Calculate visible data
    const filteredData = React.useMemo(() => {
        if (!debouncedSearchTerm) return data;
        const lowerSearch = debouncedSearchTerm.toLowerCase();
        return data.filter(item => config.fields.some(field => {
            const val = item[field.name];
            return val && String(val).toLowerCase().includes(lowerSearch);
        }));
    }, [data, debouncedSearchTerm, config.fields]);

    const visibleData = React.useMemo(
        () => filteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
        [filteredData, page, rowsPerPage],
    );

    const handleOpenDialog = useCallback((item?: any) => {
        if (item) {
            setEditItem(item);
            setFormData(item);
        } else {
            setEditItem(null);
            setFormData({
                field_name: [],
            });
        }
        setDialogOpen(true);
    }, []);

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditItem(null);
        setFormData({});
    };

    const handleInputChange = (name: string, value: any) => {
        setFormData((prev: any) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        try {
            if (editingItem) {
                await config.api.update(editingItem.id, formData);
                toast.success(`${config.label} updated successfully`);
            } else if (Array.isArray(formData.field_name) && config.api.bulkUpdate) {
                const configs = formData.field_name.map((fn: string) => ({
                    field_name: fn,
                    is_required: !!formData.is_required
                }));
                await config.api.bulkUpdate(formData.client_type, configs);
                toast.success(`${config.label} updated successfully`);
            } else {
                await config.api.create(formData);
                toast.success(`${config.label} created successfully`);
            }
            handleCloseDialog();
            fetchData();
        } catch (err) {
            console.error(err);
            toast.error(`Failed to save ${config.label}`);
        }
    };

    const handleDelete = useCallback(async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            ...confirmColors('danger'),
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await config.api.delete(id);
                toast.success(`${config.label} deleted successfully`);
                fetchData();
            } catch (err) {
                console.error(err);
                toast.error(`Failed to delete ${config.label}`);
            }
        }
    }, [config, fetchData]);

    const currentFields = useMemo(() => {
        return config.fields.map(f => {
            if (f.name === 'field_name') {
                return {
                    ...f,
                    options: availableFields,
                    multiple: !editingItem
                };
            }
            return f;
        });
    }, [config.fields, availableFields, editingItem]);

    const getDisplayValue = useCallback((field: MasterField, value: any): React.ReactNode => {
        if (field.type === 'checkbox') {
            return value ? <Pill tone="positive">Yes</Pill> : <Pill tone="neutral">No</Pill>;
        }
        if (field.type === 'select') {
            const option = field.options?.find(o => o.id === value) || availableFields.find(o => o.id === value);
            return option ? option.name : value;
        }
        return value;
    }, [availableFields]);

    const columns = useMemo<ColumnDef<any>[]>(() => [
        ...config.fields.map((field): ColumnDef<any> => ({
            key: field.name,
            header: field.label,
            align: field.type === 'number' ? 'right' : undefined,
            render: row => getDisplayValue(field, row[field.name]) || '—',
        })),
        {
            key: '__actions',
            header: 'Actions',
            width: '110px',
            render: row => (
                <div style={{ display: 'inline-flex', gap: 6 }}>
                    <Button iconOnly onClick={() => handleOpenDialog(row)} title={`Edit ${config.label}`}>
                        <Icon name="edit" size={15} />
                    </Button>
                    <Button iconOnly onClick={() => handleDelete(row.id)} title={`Delete ${config.label}`}>
                        <Icon name="trash" size={15} />
                    </Button>
                </div>
            ),
        },
    ], [config.fields, config.label, getDisplayValue, handleOpenDialog, handleDelete]);

    const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

    return (
        <>
            <Surface
                title={config.label}
                actions={
                    <Button
                        variant="primary"
                        size="sm"
                        icon={<Icon name="plus" size={14} />}
                        onClick={() => handleOpenDialog()}
                    >
                        Add New {config.label}
                    </Button>
                }
            >
                <DataTable<any>
                    embedded
                    data={visibleData}
                    columns={columns}
                    rowKey={row => row.id}
                    totalCount={filteredData.length}
                    loading={loading}
                    currentPage={page + 1}
                    totalPages={totalPages}
                    onPageChange={nextPage => setPage(nextPage - 1)}
                    pageSize={rowsPerPage}
                    pageSizeOptions={[10, 25, 50, 100]}
                    onPageSizeChange={size => { setRowsPerPage(size); setPage(0); }}
                    sortField=""
                    sortDir="asc"
                    onSort={() => undefined}
                    searchQuery={searchTerm}
                    onSearchChange={handleSearchChange}
                    searchPlaceholder={`Search ${config.label}…`}
                    compact
                    loadingMessage={`Loading ${config.label.toLowerCase()}…`}
                    emptyMessage={debouncedSearchTerm ? 'No items match that search.' : 'No items found.'}
                />
            </Surface>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle>
                    {editingItem ? `Edit ${config.label}` : `Add New ${config.label}`}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        {editingItem && (
                            <Note tone="warning">
                                <strong>Global validation change.</strong> This alters the data
                                requirements and display logic for every client of the selected type,
                                which can affect form submissions and reporting.
                            </Note>
                        )}
                        {currentFields.map(field => {
                            if (field.type === 'select') {
                                return (
                                    <FormControl key={field.name} fullWidth required={field.required}>
                                        <InputLabel>{field.label}</InputLabel>
                                        <Select
                                            value={formData[field.name] || (field.multiple ? [] : '')}
                                            label={field.label}
                                            multiple={field.multiple}
                                            onChange={(e) => handleInputChange(field.name, e.target.value)}
                                        >
                                            {field.options?.map(opt => (
                                                <MenuItem key={opt.id} value={opt.id}>
                                                    {opt.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                );
                            }
                            if (field.type === 'checkbox') {
                                return (
                                    <FormControlLabel
                                        key={field.name}
                                        control={
                                            <Checkbox
                                                checked={!!formData[field.name]}
                                                onChange={(e) => handleInputChange(field.name, e.target.checked)}
                                            />
                                        }
                                        label={field.label}
                                    />
                                );
                            }
                            return (
                                <TextField
                                    key={field.name}
                                    label={field.label}
                                    type={field.type}
                                    fullWidth
                                    required={field.required}
                                    value={formData[field.name] || ''}
                                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                                />
                            );
                        })}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid var(--border-soft)' }}>
                    <Button variant="ghost" onClick={handleCloseDialog}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave}>
                        {editingItem ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FieldConfigMaster;
